/**
 * Card - Componente de visualización de shipment en mobile
 *
 * Este componente muestra la información de un shipment en formato de tarjeta
 * para la vista mobile de Invoices.
 */
import { Badge } from '@shared/components/ui/badge';
import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { UserCard } from '@shared/components/ui/usercard';
import { Checkbox } from '@shared/components/ui/checkbox';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShipmentDropdownActions } from '../interaction';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export interface CardProps {
  item: any; // Shipment
  className?: string;
  showActions?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  selectedColumns?: string[];
}

export const Card = React.memo(function Card({
  item: shipment,
  className = '',
  showActions = true,
  isSelected,
  onToggleSelect,
  selectedColumns,
}: CardProps) {
  const { t } = useTranslation('invoicesOrdersModule');
  const currency = useCurrency(); // 🎯 Obtener moneda del team desde /me

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true;
    return selectedColumns.includes(columnId);
  };

  // 🎯 Mapeo de colores para estados financieros
  const statusColors: Record<string, { bg: string; text: string }> = {
    NOT_INVOICED: { bg: 'bg-bg-warning', text: 'text-warning' },
    PRE_INVOICED: { bg: 'bg-bg-info', text: 'text-info' },
    INVOICED: { bg: 'bg-bg-success', text: 'text-success' },
    PAID: { bg: 'bg-bg-bright-success', text: 'text-bright-success' },
    CANCELLED: { bg: 'bg-bg-error', text: 'text-error' },
  };

  const financialStatus = shipment.financial_status;
  const statusColor = statusColors[financialStatus] || { bg: 'bg-card-border', text: 'text-text' };

  return (
    <div className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors ${className}`}>
      {/* Header - Pedido, estado financiero y acciones */}
      <div className="flex items-center gap-2 mb-2 justify-between">
        <div className="flex items-center gap-2">
          <Checkbox className="w-4 h-4" checked={!!isSelected} onCheckedChange={onToggleSelect} />
          <span className="font-medium text-base">
            {shipment.ref || shipment.order?.ref || `ORD-${shipment.order_id}`}
          </span>
          {/* Estado Financiero */}
          {isColumnVisible('financial_status') && financialStatus && (
            <Badge className={`text-xs ${statusColor.bg} ${statusColor.text}`}>{financialStatus}</Badge>
          )}
        </div>
        {showActions && (
          <ShipmentDropdownActions
            shipmentId={shipment.id}
            onAction={() => {
              // TODO: Implementar acciones desde Card
            }}
          />
        )}
      </div>

      {/* Cliente - Solo si la columna está visible */}
      {isColumnVisible('customer') && shipment.customer && (
        <div className="mb-2">
          <UserCard name={shipment.customer.name} email={shipment.customer.email || ''} showEmail={true} />
        </div>
      )}

      {/* Shop - Solo si la columna está visible */}
      {isColumnVisible('shop') && shipment.shop && (
        <div className="flex items-center gap-2 mb-1">
          <ShopComponent name={shipment.shop.name} src={shipment.shop.image_hash} />
        </div>
      )}

      {/* Courier - Solo si la columna está visible */}
      {isColumnVisible('courier') && shipment.courier && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">Courier:</span>
          <span className="text-sm font-medium">{shipment.courier.name}</span>
        </div>
      )}

      {/* Fecha de envío */}
      {isColumnVisible('shipping_date') && shipment.shipping_date && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.shipment_date')}:</span>
          <span className="text-sm">{formatDate(shipment.shipping_date.date)}</span>
        </div>
      )}

      {/* Estado Interno */}
      {isColumnVisible('internal_status') && (shipment.internal_status || shipment.status) && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.status')}:</span>
          <Badge className="text-xs bg-card-border text-text">
            {shipment.internal_status?.description || shipment.status?.description}
          </Badge>
        </div>
      )}

      {/* Estado Logístico */}
      {isColumnVisible('logistic_status') && shipment.logistic_status && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.logistic_status')}:</span>
          <Badge className="text-xs bg-card-border text-text">{shipment.logistic_status.description}</Badge>
        </div>
      )}

      {/* Referencia */}
      {isColumnVisible('reference') && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.reference')}:</span>
          <span className="text-sm">{shipment.ref || shipment.order?.ref || '-'}</span>
        </div>
      )}

      {/* Precio del Pedido */}
      {isColumnVisible('order_price') && shipment.order && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.total')}:</span>
          <span className="text-sm font-medium">
            {new Intl.NumberFormat('es-ES', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(shipment.order.total || 0)}{' '}
            {currency}
          </span>
        </div>
      )}

      {/* N° Pre-Factura */}
      {isColumnVisible('pre_invoice_id') && (shipment.pre_invoice?.code || shipment.pre_invoice_id) && (
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-complementary-text">{t('table.pre_invoice_no')}:</span>
          <span className="text-sm font-medium">{shipment.pre_invoice?.code ?? `#${shipment.pre_invoice_id}`}</span>
        </div>
      )}

      {/* N° Factura */}
      {isColumnVisible('invoice_id') &&
        (shipment.invoice?.code || shipment.invoice?.invoice_number || shipment.invoice_id) && (
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm text-complementary-text">{t('table.invoice_no')}:</span>
            <span className="text-sm font-medium">
              {shipment.invoice?.code ?? shipment.invoice?.invoice_number ?? `#${shipment.invoice_id}`}
            </span>
          </div>
        )}
    </div>
  );
});
